<template>
  <div class="history-page">
    <header class="history-header">
      <button type="button" class="history-header__btn" aria-label="返回" @click="goBack">
        ‹
      </button>
      <h1 class="history-header__title">历史开奖</h1>
      <button type="button" class="history-header__btn history-header__btn--icon" aria-label="日历">
        <span class="history-header__cal" aria-hidden="true" />
      </button>
    </header>

    <nav class="history-tabs" role="tablist" aria-label="历史类型">
      <button
        type="button"
        role="tab"
        class="history-tabs__btn"
        :class="{ 'is-active': mainTab === 'records' }"
        :aria-selected="mainTab === 'records'"
        @click="mainTab = 'records'"
      >
        开奖记录
      </button>
 
    </nav>

   

    <main class="history-body">
      <p v-if="mainTab === 'dates'" class="history-placeholder">开奖日期视图开发中</p>
      <template v-else>
        <p v-if="listLoading && rows.length === 0" class="history-status">加载中…</p>
        <p v-else-if="listError" class="history-status history-status--error">{{ listError }}</p>
        <p v-else-if="!listLoading && rows.length === 0" class="history-status">暂无开奖记录</p>
        <ul v-else class="history-list">
          <li v-for="row in sortedRows" :key="row.rowKey" class="history-item">
          <div class="history-item__head">
            <span class="history-item__issue">第{{ row.issue }}期开奖结果</span>
            <span class="history-item__date">{{ row.dateLabel }}</span>
          </div>
          <div class="history-item__balls">
            <div
              v-for="(b, i) in row.mainNumbers"
              :key="'m-' + row.issue + '-' + i"
              class="history-ball-col"
            >
              <div class="history-ball" :class="'history-ball--' + b.tone">
                <span class="history-ball__num">{{ b.value }}</span>
              </div>
              <div class="history-ball__meta">{{ metaLine(b) }}</div>
            </div>
            <div class="history-ball-col history-ball-col--plus">
              <span class="history-plus">+</span>
            </div>
            <div class="history-ball-col">
              <div class="history-ball" :class="'history-ball--' + row.special.tone">
                <span class="history-ball__num">{{ row.special.value }}</span>
              </div>
              <div class="history-ball__meta">{{ metaLine(row.special) }}</div>
            </div>
          </div>
          </li>
        </ul>
        <div v-if="mainTab === 'records' && !isMacauHistory && rows.length > 0 && hasMore" class="history-more">
          <button
            type="button"
            class="history-more__btn"
            :disabled="listLoadingMore"
            @click="loadMore"
          >
            {{ listLoadingMore ? '加载中…' : '加载更多' }}
          </button>
        </div>
      </template>
    </main>
  </div>
</template>

<script>
import { fetchDrawsPage } from '@/api/draw'
import { fetchMacauHistoryYear } from '@/api/macauHistory'
import { fetchHkHistory } from '@/api/hkHistory'
import { GAME_ID_TO_DRAW_TYPE } from '@/config/gameConfig'
import { extractPagePayload } from '@/utils/httpUtils'
import {
  mapMacauHistoryItem,
  mapRecordToRow,
  mapHkHistoryRecordToRow,
  dedupeMacauMappedRows,
  filterMacauListByYear,
  extractMacauDataList
} from '@/services/historyAdapters'
import { elementOf } from '@/utils/ballUtils'

export default {
  name: 'HistoryDrawPage',
  data() {
    return {
      mainTab: 'records',
      /** desc | wuxing | default：与示意图一致，仅排序/展示策略不同 */
      listMode: 'wuxing',
      rows: [],
      listLoading: false,
      listLoadingMore: false,
      listError: null,
      page: 1,
      pageSize: 25,
      total: 0,
      /** 接口未返回 total 时，用「本页是否满页」判断是否可能还有下一页 */
      lastFetchHadFullPage: false,
      /** 澳门：仅请求当年接口，不再加载上一年 */
      macauCurrentYear: new Date().getFullYear()
    }
  },
  computed: {
    gameId() {
      return (this.$route.query.game && String(this.$route.query.game)) || ''
    },
    isMacauHistory() {
      return this.gameId === 'macau'
    },
    /** 香港经典玩法 tab「hk」：走库表 hk_lot_draws 接口 */
    isHkLotDrawsList() {
      return this.gameId === 'hk'
    },
    drawTypeParam() {
      return GAME_ID_TO_DRAW_TYPE[this.gameId] || this.gameId
    },
    yearLabel() {
      const y = this.rows[0] && this.rows[0].dateLabel
      const m = y && y.match(/(\d{4})年/)
      return (m && m[1] ? m[1] : new Date().getFullYear()) + '年历史开奖记录'
    },
    sortedRows() {
      const copy = [...this.rows]
      if (this.listMode === 'default') {
        copy.sort((a, b) => a.issueNum - b.issueNum)
      } else {
        copy.sort((a, b) => b.issueNum - a.issueNum)
      }
      return copy
    },
    hasMore() {
      if (this.isMacauHistory) return false
      if (this.total > 0) return this.rows.length < this.total
      return this.lastFetchHadFullPage
    }
  },
  watch: {
    '$route.query.game'() {
      this.reloadList()
    }
  },
  mounted() {
    this.reloadList()
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push({ name: 'home' })
    },
    metaLine(b) {
      if (!b) return ''
      return [b.zodiac, b.element || elementOf(b.value)].filter(Boolean).join('/')
    },
    reloadList() {
      this.page = 1
      this.total = 0
      this.rows = []
      this.lastFetchHadFullPage = false
      this.listError = null
      if (this.isMacauHistory) {
        this.macauCurrentYear = new Date().getFullYear()
        this.fetchMacauYearBatch(this.macauCurrentYear)
      } else if (this.isHkLotDrawsList) {
        this.fetchHkLotDrawsPage(1, false)
      } else {
        this.fetchPage(1, false)
      }
    },
    loadMore() {
      if (this.isMacauHistory) return
      if (!this.hasMore || this.listLoadingMore || this.listLoading) return
      if (this.isHkLotDrawsList) {
        this.fetchHkLotDrawsPage(this.page + 1, true)
      } else {
        this.fetchPage(this.page + 1, true)
      }
    },
    async fetchMacauYearBatch(year) {
      this.listLoading = true
      this.listError = null
      try {
        const res = await fetchMacauHistoryYear(year)
        let list = extractMacauDataList(res)
        list = filterMacauListByYear(list, year)
        const mapped = []
        for (let i = 0; i < list.length; i++) {
          const row = mapMacauHistoryItem(list[i])
          if (row) mapped.push(row)
        }
        this.rows = dedupeMacauMappedRows(mapped)
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) ||
          e.message ||
          '加载失败'
        this.listError = String(msg)
        this.rows = []
      } finally {
        this.listLoading = false
      }
    },
    async fetchHkLotDrawsPage(nextPage, append) {
      if (this.mainTab !== 'records') return
      if (append) this.listLoadingMore = true
      else this.listLoading = true
      this.listError = null
      try {
        const year = new Date().getFullYear()
        const res = await fetchHkHistory({ pageNum: nextPage, year })
        const body = res && res.data
        if (!body || body.code !== 10000) {
          throw new Error((body && body.msg) || '接口返回失败')
        }
        const d = body.data || {}
        const list = Array.isArray(d.recordList) ? d.recordList : []
        const pager = d.pager || {}

        const mapped = []
        for (let i = 0; i < list.length; i++) {
          const row = mapHkHistoryRecordToRow(list[i], i)
          if (row) mapped.push(row)
        }
        if (append) {
          this.rows = this.rows.concat(mapped)
        } else {
          this.rows = mapped
        }
        this.total = pager.totalCount > 0 ? pager.totalCount : 0
        this.page = nextPage
        const size = pager.pageSize || 10
        this.pageSize = Math.min(200, size)
        this.lastFetchHadFullPage = mapped.length >= size
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) ||
          e.message ||
          '加载失败'
        this.listError = String(msg)
        if (!append) this.rows = []
      } finally {
        this.listLoading = false
        this.listLoadingMore = false
      }
    },
    async fetchPage(nextPage, append) {
      if (this.mainTab !== 'records') return
      if (append) this.listLoadingMore = true
      else this.listLoading = true
      this.listError = null
      try {
        const res = await fetchDrawsPage({
          type: this.drawTypeParam,
          page: nextPage,
          size: this.pageSize
        })
        const { list, total, size } = extractPagePayload(res)
        const mapped = []
        for (let i = 0; i < list.length; i++) {
          const row = mapRecordToRow(list[i], i)
          if (row) mapped.push(row)
        }
        if (append) {
          this.rows = this.rows.concat(mapped)
        } else {
          this.rows = mapped
        }
        this.total = total
        this.page = nextPage
        if (size && Number(size) > 0) this.pageSize = Math.min(200, Number(size))
        const reqSize = Math.min(200, this.pageSize)
        this.lastFetchHadFullPage = mapped.length >= reqSize
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) ||
          e.message ||
          '加载失败'
        this.listError = String(msg)
        if (!append) this.rows = []
      } finally {
        this.listLoading = false
        this.listLoadingMore = false
      }
    }
  }
}
</script>

<style scoped>
.history-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: env(safe-area-inset-bottom, 0);
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: max(0.5rem, env(safe-area-inset-top)) clamp(0.5rem, 2.5vw, 0.75rem) 0.625rem;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.history-header__title {
  margin: 0;
  flex: 1;
  text-align: center;
  font-size: clamp(1rem, 4vw, 1.0625rem);
  font-weight: 700;
  color: #1a202c;
}

.history-header__btn {
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background: transparent;
  font-size: 1.75rem;
  line-height: 1;
  color: #2d3748;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  -webkit-tap-highlight-color: transparent;
}

.history-header__btn--icon {
  font-size: 1rem;
}

.history-header__cal {
  display: block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #38a169;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px rgba(56, 161, 105, 0.25);
}

.history-tabs {
  display: flex;
  justify-content: center;
  gap: clamp(1.5rem, 8vw, 2.5rem);
  padding: 0.75rem 1rem 0.625rem;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.history-tabs__btn {
  border: none;
  background: none;
  font-size: clamp(0.9375rem, 3.5vw, 1rem);
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  padding: 0.25rem 0;
  position: relative;
  -webkit-tap-highlight-color: transparent;
}

.history-tabs__btn.is-active {
  color: #38a169;
}

.history-tabs__btn.is-active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -0.5rem;
  height: 2px;
  background: #38a169;
  border-radius: 1px;
}

.history-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.625rem clamp(0.75rem, 3vw, 1rem);
  background: #e8edf2;
}

.history-toolbar__year {
  font-size: clamp(0.8125rem, 3vw, 0.875rem);
  font-weight: 600;
  color: #2d3748;
}

.history-toolbar__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-end;
}

.history-pill {
  border: 1px solid #cbd5e0;
  background: #fff;
  color: #718096;
  font-size: clamp(0.6875rem, 2.8vw, 0.75rem);
  font-weight: 600;
  padding: 0.35rem 0.55rem;
  border-radius: 999px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.history-pill.is-active {
  background: #38a169;
  border-color: #38a169;
  color: #fff;
}

.history-body {
  padding: 0.625rem clamp(0.5rem, 2.5vw, 0.75rem) 1.25rem;
}

.history-placeholder {
  margin: 2rem 1rem;
  text-align: center;
  color: #718096;
  font-size: 0.875rem;
}

.history-status {
  margin: 2rem 1rem;
  text-align: center;
  font-size: clamp(0.8125rem, 3vw, 0.875rem);
  color: #718096;
}

.history-status--error {
  color: #c53030;
}

.history-more {
  padding: 0.25rem 1rem 1.25rem;
  display: flex;
  justify-content: center;
}

.history-more__btn {
  border: 1px solid #38a169;
  background: #fff;
  color: #276749;
  font-size: clamp(0.8125rem, 3vw, 0.875rem);
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  border-radius: 999px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.history-more__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.history-item {
  background: #fff;
  border-radius: 10px;
  padding: 0.625rem 0.75rem 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.history-item__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.history-item__issue {
  font-size: clamp(0.8125rem, 3vw, 0.875rem);
  font-weight: 700;
  color: #1a365d;
  line-height: 1.3;
}

.history-item__date {
  flex-shrink: 0;
  font-size: clamp(0.75rem, 2.8vw, 0.8125rem);
  color: #718096;
}

.history-item__balls {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.15rem;
  padding: 0.5rem 0.35rem;
  border: 1px solid #48bb78;
  border-radius: 8px;
  background: #fafcfa;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.history-ball-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  flex: 0 0 auto;
  min-width: 0;
}

.history-ball-col--plus {
  justify-content: center;
  padding-top: 0.35rem;
}

.history-plus {
  font-weight: 800;
  color: #4a5568;
  font-size: clamp(0.9rem, 3.5vw, 1rem);
}

.history-ball {
  box-sizing: border-box;
  width: clamp(1.85rem, 9vw, 2.15rem);
  height: clamp(1.85rem, 9vw, 2.15rem);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.16);
}

.history-ball--red {
  background-image: url('~@/images/red.png');
}

.history-ball--blue {
  background-image: url('~@/images/bule.png');
}

.history-ball--green {
  background-image: url('~@/images/green.png');
}

.history-ball__num {
  display: inline-block;
  font-size: clamp(0.7rem, 3.2vw, 0.8rem);
  font-weight: 800;
  color: #0d0d0d;
  line-height: 1;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.55);
  transform: translate(clamp(-0.06rem, -0.35vw, -0.03rem), clamp(-0.1rem, -0.45vw, -0.06rem));
}

.history-ball__meta {
  font-size: clamp(0.75rem, 3.4vw, 0.9rem);
  font-weight: 600;
  color: #2d3748;
  white-space: nowrap;
}
</style>
